import { SegmentReporting } from '../signals-insights.service';

/**
 * Static configuration for the Signals Flow (Traffic -> Segment -> Actionable
 * value) board.
 *
 * Segments and sub-segments come from `/behavioral-intelligence/segments`; the
 * component maps that response onto the `FlowFamily` / `FlowSegment` shapes
 * below. Destination labels and segment ideas are static configuration. Campaigns and
 * performance are loaded from the campaign APIs.
 */

export type SegmentStateKey = 'running' | 'off' | 'proposed';
export type DestinationGroup = 'onsite' | 'offsite';
export type DestinationSlot = 'message' | 'page' | 'human';
export type GoalKey = 'checkout' | 'engage' | 'aov';

export interface FlowSegment {
  /** Unique across the board: `<stage value>:<slug>`, or the id for customs. */
  id: string;
  /** The API item `value`; used to look up local placeholder data. */
  slug: string;
  /** The API `selectedStage` this segment belongs to ('' for custom ones). */
  stage: string;
  name: string;
  tint: string;
  icon: string;
  /**
   * Share of the stage's sessions, in percent. The figures are null when
   * they are not available for the filters (`metricsStatus: unavailable`)
   * — shown as "—", never as zero.
   */
  share: number | null;
  sessions: number | null;
  revenue: number | null;
  /** Pre-formatted `revenue` ("—" when unavailable), so the template stays free of pipes. */
  value: string;
  state: SegmentStateKey;
  /** Destination ids this segment is wired to, from the API. */
  channels: string[];
  goal: GoalKey;
  /** Score threshold sent with the workflow rule. */
  threshold: number;
  desc?: string;
  /** A custom stage: wired with `segment: null` and editable/deletable. */
  custom?: boolean;
  canDelete?: boolean;
  /** Custom stages: the key `/config/stage` PATCH and DELETE expect. */
  key?: string;
  /** Custom stages: who created it, from `userDetails`. */
  author?: string;
  /** Saved event (audience) name per channel, when the API returns it. */
  eventNames?: Record<string, string>;
  /**
   * Custom stages: collection mode, status and per-source figures. Custom
   * cards read their metrics from here, never from `sessions` / `revenue`.
   */
  reporting?: SegmentReporting;
}

export interface FlowFamily {
  /** The API `value`, e.g. `purchase_journey_stages`. */
  id: string;
  /** The API `selectedStage`, e.g. `purchaseJourneyStages`. */
  stage: string;
  name: string;
  tint: string;
  icon: string;
  meta: string;
  desc?: string;
  /** null when any child's figure is unavailable: a partial sum would read as complete */
  sessions: number | null;
  revenue: number | null;
  children: FlowSegment[];
  /** Custom stages carry no items and act as a segment in their own right. */
  self?: FlowSegment;
}

export interface FlowDestination {
  id: string;
  group: DestinationGroup;
  name: string;
  tint: string;
  icon: string;
  slot?: DestinationSlot;
  latency: string;
  share: string;
  cost?: string;
  /** Off-site only: whether the integration is connected for this target. */
  connected?: boolean;
}

export interface NudgePattern {
  code: string;
  kpi: string;
  headline: string;
  desc: string;
  principle: string;
  placement: string;
  sites: number;
  tags: string[];
}

export interface PerformanceRecord {
  reach: string;
  conv: string;
  cvr: string;
  rev: string;
  uplift: string;
  conf: string;
  sig: boolean;
  sync: string;
  syncState: 'ok' | 'warn' | 'bad';
  subtitle: string;
  /** [label, absolute value, percent of the first step] */
  funnel: Array<[string, number, number]>;
  verdict: string;
  action: string;
}

export interface FlowProposal {
  id: string;
  name: string;
  /** Sent as `description` when the proposal is turned into a custom stage. */
  description: string;
  threshold: number;
  tint: string;
  icon: string;
  sub: string;
}

/** 24x24 icon paths, drawn as a single stroked <path>. */
export const FLOW_ICONS = {
  swap: 'M4 8h13l-3-3M20 16H7l3 3',
  loop: 'M20 12a8 8 0 1 1-2.4-5.7M20 4v4h-4',
  eye: 'M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6zM12 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4z',
  warn: 'M12 3 2 20h20zM12 9v5M12 17.4v.2',
  cluster: 'M8 8h.01M16 8h.01M12 16h.01M8 8a1 1 0 1 0 0 .01',
  bell: 'M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6M10 20h4',
  meta: 'M4 12c0-3 1.7-4.5 3.5-4.5S11 11 12 13s2.5 4 4.5 4S20 15 20 12s-1.5-4.5-3.5-4.5S13 11 12 13s-2.5 4-4.5 4S4 15 4 12z',
  mail: 'M3 6h18v12H3zM3 7l9 6 9-6',
  cursor: 'M5 3l14 7-6 2-2 6z',
  person: 'M4 20a8 8 0 0 1 16 0M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  tag: 'M3 12V4h8l9 9-8 8zM7.5 7.5h.01',
  ruler: 'M3 9h18v6H3zM7 9v3M11 9v4M15 9v3M19 9v4',
  layout: 'M3 4h18v16H3zM3 9h18M9 9v11',
  mute: 'M11 5 6 9H3v6h3l5 4zM22 9l-6 6M16 9l6 6',
  chat: 'M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12z',
  brain: 'M12 5a3 3 0 0 0-6 0 3 3 0 0 0-1 5.8A3 3 0 0 0 8 16h4zM12 5a3 3 0 0 1 6 0 3 3 0 0 1 1 5.8A3 3 0 0 1 16 16h-4z',
  route: 'M6 20V8a3 3 0 0 1 3-3h6M18 4v12a3 3 0 0 1-3 3H9M6 20l-2-2M6 20l2-2M18 4l-2 2M18 4l2 2',
  exit: 'M14 3h6v18h-6M10 12H3M7 9l-4 3 4 3',
  star: 'M12 3l2.6 5.6 6.4.8-4.7 4.3 1.2 6.3-5.5-3-5.5 3 1.2-6.3L3 9.4l6.4-.8z',
  shop: 'M3 9h18l-1.5-4.5h-15zM4.5 9v10.5h15V9M9 19.5V14h6v5.5',
  check: 'M5 13l4 4L19 7',
  plus: 'M12 6v12M6 12h12',
  chevron: 'M9 6l6 6-6 6',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14zM20 20l-4-4',
  copy: 'M9 7H5v12h14V7h-4M9 7V5h6v2M9 7h6',
  info: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18M12 11v5M12 7.6v.2',
  pencil: 'M4 20h4L20 8l-4-4L4 16zM14 6l4 4',
  trash: 'M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13',
} as const;

/** Look and feel per stage, matched on the API `value`. */
export const FAMILY_STYLES: Record<string, { tint: string; icon: string }> = {
  purchase_journey_stages: { tint: '#2fb98d', icon: FLOW_ICONS.route },
  psych_affinities: { tint: '#7c5cf0', icon: FLOW_ICONS.brain },
  shopping_behavior_profile: { tint: '#4f7cf7', icon: FLOW_ICONS.person },
  abandon_reasons: { tint: '#f2a03d', icon: FLOW_ICONS.warn },
  value_flow_stage_sessions: { tint: '#ec4b8c', icon: FLOW_ICONS.cluster },
};

/** Fallback look for stages this build does not know about yet. */
export const FAMILY_FALLBACK_TINTS = [
  '#2fb98d',
  '#7c5cf0',
  '#4f7cf7',
  '#f2a03d',
  '#ec4b8c',
  '#4fc3dd',
];

/** Cycled through for sub-segment cards, in response order. */
export const SEGMENT_ICON_CYCLE = [
  FLOW_ICONS.swap,
  FLOW_ICONS.loop,
  FLOW_ICONS.eye,
  FLOW_ICONS.cluster,
  FLOW_ICONS.tag,
  FLOW_ICONS.warn,
  FLOW_ICONS.exit,
  FLOW_ICONS.layout,
  FLOW_ICONS.person,
  FLOW_ICONS.ruler,
  FLOW_ICONS.chat,
  FLOW_ICONS.star,
  FLOW_ICONS.mute,
];

/**
 * Onsite treatments are fixed - they are executed by Nudge rather than by a
 * connected integration, so they do not appear in the channels endpoint.
 */
export const ONSITE_DESTINATIONS: FlowDestination[] = [
  {
    id: 'nudge',
    group: 'onsite',
    name: 'Nudge Experiment',
    tint: '#f2a03d',
    icon: FLOW_ICONS.bell,
    slot: 'message',
    latency: 'Create and measure a segment campaign',
    share: 'Onsite message · no media cost',
    cost: 'no media cost',
  },
  {
    id: 'incentive',
    group: 'onsite',
    name: 'Incentive / Coupon',
    tint: '#ec4b8c',
    icon: FLOW_ICONS.tag,
    slot: 'message',
    latency: 'Test your existing offer or coupon',
    share: 'Onsite message · costs margin',
    cost: 'margin cost',
  },
  {
    id: 'personalize',
    group: 'onsite',
    name: 'Content Personalization',
    tint: '#4f7cf7',
    icon: FLOW_ICONS.layout,
    slot: 'page',
    latency: 'Silent · re-ranks the page',
    share: 'Silent · re-ranks the page',
    cost: 'no interruption',
  },
  {
    id: 'assist',
    group: 'onsite',
    name: 'Assisted / Live Handoff',
    tint: '#2fb98d',
    icon: FLOW_ICONS.chat,
    slot: 'human',
    latency: 'Routes to a human · high value only',
    share: 'Routes to a human',
    cost: 'agent time',
  },
];

/**
 * Look and feel per off-site channel, matched on the `value` returned by
 * `/behavioral-intelligence/channels`. Unknown channels fall back to the
 * generic style below, so a new integration still renders.
 */
export interface OffsiteChannelStyle {
  tint: string;
  icon: string;
  latency: string;
  /** One line under the title of the channel's drawer. */
  summary?: string;
  /** What the user still has to do in the channel after activating. */
  steps?: string[];
}

export const OFFSITE_CHANNEL_STYLES: Record<string, OffsiteChannelStyle> = {
  meta: {
    tint: '#2fb98d',
    icon: FLOW_ICONS.meta,
    latency: 'Live in Meta in 24–48h',
    summary: 'Becomes a Custom Audience you can target in Meta ads.',
    steps: [
      'Open your audience {audience} in Meta Audiences.',
      'Accept the external events from Behamics.',
      'Matching then takes 24–48 hours.',
    ],
  },
  google_ads: {
    tint: '#4f7cf7',
    icon: FLOW_ICONS.cursor,
    latency: '6–48h to populate',
    summary: 'Becomes an audience you can target in Google Ads.',
  },
  klaviyo: {
    tint: '#7c5cf0',
    icon: FLOW_ICONS.mail,
    latency: 'Sends within minutes',
    summary: 'Matching shoppers are passed to Klaviyo for your flows.',
  },
  monday_crm: {
    tint: '#ec4b8c',
    icon: FLOW_ICONS.person,
    latency: 'Pushed hourly',
    summary: 'Matching visitors are pushed to your monday CRM board.',
  },
  linkedin_realtime_signals: {
    tint: '#0a66c2',
    icon: FLOW_ICONS.cursor,
    latency: 'Streams in real time',
    summary: 'Matching visits stream to LinkedIn as real-time signals.',
  },
  emarsys: {
    tint: '#008fd3',
    icon: FLOW_ICONS.person,
    latency: 'Updates contact fields',
    summary: 'Scores are kept up to date on identified SAP Emarsys contacts.',
  },
  hubspot: {
    tint: '#ff7a59',
    icon: FLOW_ICONS.person,
    latency: 'Updates contact properties',
    summary: 'Scores are written to a number property on matching HubSpot contacts.',
  },
};

export const OFFSITE_CHANNEL_FALLBACK: OffsiteChannelStyle = {
  tint: '#6c6d7a',
  icon: FLOW_ICONS.cursor,
  latency: 'Pushed on a schedule',
};

export const NUDGE_LIBRARY: NudgePattern[] = [
  {
    code: '2.6.2.1',
    kpi: 'Conversion rate',
    headline: 'This is currently one of our most popular articles',
    desc: 'Shows what other shoppers prefer and reduces purchase hesitation at the decision point.',
    principle: 'Social proof',
    placement: 'Product detail',
    sites: 617,
    tags: ['hesitation', 'compare'],
  },
  {
    code: '4.1.4.2',
    kpi: 'Conversion rate',
    headline: "Good decision, that's a great choice!",
    desc: "Reinforces consistency with the shopper's choice and reduces purchase hesitation.",
    principle: 'Choice reassurance',
    placement: 'Cart',
    sites: 611,
    tags: ['hesitation', 'loop'],
  },
  {
    code: '1.100.40.2',
    kpi: 'Conversion rate',
    headline: 'Your shopping cart is ready.',
    desc: 'Highlights what may be lost by waiting or leaving and reduces purchase hesitation.',
    principle: 'Loss aversion',
    placement: 'Cart',
    sites: 596,
    tags: ['abandon', 'hesitation'],
  },
  {
    code: '3.2.8.4',
    kpi: 'Checkout completion',
    headline: 'Almost there — one step left.',
    desc: 'Reduces perceived effort at the payment step for sessions that stall before paying.',
    principle: 'Goal gradient',
    placement: 'Checkout',
    sites: 428,
    tags: ['abandon', 'friction'],
  },
  {
    code: '5.4.1.9',
    kpi: 'Checkout completion',
    headline: 'Having trouble? Checkout in one step instead.',
    desc: 'Offers a shorter path after a form or payment error is detected in session.',
    principle: 'Friction removal',
    placement: 'Checkout',
    sites: 311,
    tags: ['friction'],
  },
  {
    code: '6.1.2.7',
    kpi: 'Engagement rate',
    headline: 'Still deciding? Here they are side by side.',
    desc: 'Surfaces a comparison of the products the shopper keeps returning to.',
    principle: 'Choice architecture',
    placement: 'Product detail',
    sites: 274,
    tags: ['loop', 'compare'],
  },
  {
    code: '7.3.5.1',
    kpi: 'Average order value',
    headline: 'Customers usually add this too.',
    desc: 'Bundle prompt at add-to-cart, tuned for basket size rather than conversion.',
    principle: 'Anchoring',
    placement: 'Cart',
    sites: 389,
    tags: ['impulse'],
  },
  {
    code: '8.2.2.3',
    kpi: 'Engagement rate',
    headline: 'Most shoppers in your size picked this one.',
    desc: 'Social proof tuned to uncertainty around fit and variant choice.',
    principle: 'Social proof',
    placement: 'Product detail',
    sites: 203,
    tags: ['size', 'hesitation'],
  },
];

/** Behaviour tags per API segment slug, used to rank nudge patterns. */
export const SEGMENT_TAGS: Record<string, string[]> = {
  // purchase_journey_stages
  ready_to_buy: ['impulse'],
  weighing_up: ['compare', 'loop'],
  exploring: ['loop'],
  just_looking: ['loop'],
  slipping_away: ['abandon'],
  // psych_affinities
  social_norm: ['hesitation'],
  trust_sensitivity: ['hesitation'],
  informational_nudging: ['compare'],
  urgency_affinity: ['abandon'],
  scarcity: ['abandon'],
  loss_aversion: ['abandon', 'hesitation'],
  commitment: ['loop'],
  anchoring: ['impulse'],
  authority: ['hesitation'],
  default_effect: ['impulse'],
  reciprocity: ['impulse'],
  sustainability_orientation: ['loop'],
  // shopping_behavior_profile
  exploration_oriented_shopper: ['loop'],
  impulse_buying_propensity: ['impulse'],
  goal_oriented_shopper: ['hesitation'],
  brand_loyalty: ['impulse'],
  discount_affinity: ['abandon'],
  coupon_sensitivity: ['abandon'],
  // abandon_reasons
  low_intent_browsing: ['loop'],
  insufficient_product_interest: ['loop'],
  checkout_abandonment: ['abandon', 'friction'],
  comparing_elsewhere: ['compare', 'hesitation'],
  price_sensitivity: ['abandon'],
  distraction_or_inactivity: ['loop'],
  availability_issue: ['hesitation'],
  size_or_color_uncertainty: ['size', 'hesitation'],
  // value_flow_stage_sessions
  product_evaluation: ['hesitation', 'compare'],
  single_product_view: ['loop'],
  smooth_purchase: ['impulse'],
  product_comparison: ['compare', 'loop'],
  decision_loop: ['loop', 'compare'],
  cognitive_dissonance: ['hesitation', 'compare'],
  buying_signal_cluster: ['impulse'],
};

export const GOAL_KPI: Record<GoalKey, string> = {
  checkout: 'Checkout completion',
  engage: 'Engagement rate',
  aov: 'Average order value',
};

export const FLOW_GOALS: Array<{ id: GoalKey; label: string }> = [
  { id: 'checkout', label: 'Checkout completion' },
  { id: 'engage', label: 'Engagement' },
  { id: 'aov', label: 'Average order value' },
];

export const SEGMENT_STATES: Record<
  SegmentStateKey,
  { label: string; tone: string }
> = {
  running: { label: 'Live', tone: 'ok' },
  off: { label: 'Off', tone: 'warn' },
  proposed: { label: 'New', tone: 'accent' },
};

/**
 * Suggested custom stages. Still local - there is no discovery endpoint yet -
 * but adding one now creates a real stage through `/config/stage`.
 */
export const FLOW_PROPOSALS: FlowProposal[] = [
  {
    id: 'repeat_size_checker',
    name: 'Repeat Size Checker',
    description:
      'Visitors who repeatedly open the size guide or switch between sizes on the same product without adding to cart.',
    threshold: 55,
    tint: '#4f7cf7',
    icon: FLOW_ICONS.ruler,
    sub: 'Size hesitation · suggested threshold 55',
  },
  {
    id: 'cart_ready_payment_shy',
    name: 'Cart Ready, Payment Shy',
    description:
      'Visitors who reach the payment step with a filled cart and then stall or return to the cart without paying.',
    threshold: 65,
    tint: '#7c5cf0',
    icon: FLOW_ICONS.swap,
    sub: 'Payment hesitation · suggested threshold 65',
  },
];

/** Keyed by `<API segment slug>|<destination id>`. */
